/**
 * StuckOcrBanner — zeigt sich wenn eine Karte in `ocr_running` festhängt
 * (>15min ohne updated_at-Bewegung). Anlassfall 2026-05-19: pg-boss-Handler
 * stirbt im OCR-Sidecar-Connect, Job wird re-claimed + early-returnt weil
 * state schon ocr_running ist → DB-state-Recycling fehlt, Karte hängt für
 * immer ohne UI-Signal.
 *
 * Statt automatischem Reaper (würde stillschweigend recyclen) macht der
 * Banner sichtbar dass die Karte gestoppt ist. User klickt Restart-Button,
 * Hub setzt state zurück auf ocr_pending + enqueued neuen card.ocr Job.
 *
 * Sichtbarkeit gated auf `card.stuck_ocr=true` (server-computed).
 */
import { useState, useEffect } from "react";
import type { Card, StuckDiagnosis, StuckHypothesis } from "../../lib/api";
import { api } from "../../lib/api";
import { stepUuid } from "../../lib/stepUuid";

const HYPOTHESIS_STYLES: Record<StuckHypothesis, { color: string; bg: string; icon: string; label: string }> = {
  running_normal:  { color: "#065f46", bg: "#ecfdf5", icon: "▶", label: "OCR läuft normal" },
  running_long:    { color: "#92400e", bg: "#fefce8", icon: "⏱", label: "OCR läuft sehr lange" },
  sidecar_silent:  { color: "#991b1b", bg: "#fef2f2", icon: "⚠", label: "Sidecar antwortet nicht" },
  worker_crashed:  { color: "#991b1b", bg: "#fef2f2", icon: "💀", label: "Worker abgestürzt" },
  no_job:          { color: "#92400e", bg: "#fef3c7", icon: "❓", label: "Kein Job in Queue" },
  not_stuck:       { color: "#374151", bg: "#f3f4f6", icon: "✓", label: "Kein Stuck" },
};

interface Props {
  card: Card;
  /** Nach erfolgreichem Restart aufgerufen — Werkstatt re-fetcht die Card. */
  onRestart?: () => void;
}

export function StuckOcrBanner({ card, onRestart }: Props) {
  const [phase, setPhase] = useState<"idle" | "restarting" | "done" | "error">("idle");
  const [error, setError] = useState<string | null>(null);
  const [lastJob, setLastJob] = useState<string | null>(null);
  const [diag, setDiag] = useState<StuckDiagnosis | null>(null);
  const [diagLoading, setDiagLoading] = useState(false);

  // Lazy-load diagnose beim Mount + alle 10s polling solange banner sichtbar.
  // Beantwortet: läuft OCR noch oder echter Stillstand?
  useEffect(() => {
    if (!card.stuck_ocr) return;
    let alive = true;
    const fetchOnce = async () => {
      setDiagLoading(true);
      try {
        const d = await api.cards.stuckDiagnosis(card.id);
        if (alive) setDiag(d);
      } catch { /* silent — Diagnose ist Bonus */ }
      finally { if (alive) setDiagLoading(false); }
    };
    fetchOnce();
    const t = setInterval(fetchOnce, 10000);
    return () => { alive = false; clearInterval(t); };
  }, [card.id, card.stuck_ocr]);

  if (!card.stuck_ocr) return null;
  const stuckMin = Math.round(card.stuck_minutes ?? 0);
  const stateUuid = stepUuid(card.id, "state", "ocr_running");
  const hStyle = diag ? HYPOTHESIS_STYLES[diag.hypothesis] : null;

  async function restart() {
    setPhase("restarting");
    setError(null);
    try {
      const res = await api.cards.restartOcr(card.id);
      setLastJob(res.job_id);
      setPhase("done");
      onRestart?.();
    } catch (e: any) {
      setError(e?.message ?? String(e));
      setPhase("error");
    }
  }

  // Banner-Header-Farbe nach Hypothese: grün=läuft normal, gelb=läuft lange (Sidecar
  // rechnet, KEIN Absturz), rot=echtes Problem (worker_crashed/sidecar_silent/no_job).
  const bannerBg = hStyle?.bg ?? "#fef2f2";
  const bannerColor = hStyle?.color ?? "#7f1d1d";
  const bannerBorder = diag?.hypothesis === "running_normal" ? "#86efac"
    : diag?.hypothesis === "running_long" ? "#fde68a"
    : "#fca5a5";

  return (
    <div style={{
      background: bannerBg, color: bannerColor,
      border: `1px solid ${bannerBorder}`,
      borderRadius: 4, padding: 12, marginBottom: 12,
      display: "flex", flexDirection: "column", gap: 8,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
        <strong>
          {hStyle ? `${hStyle.icon} ${hStyle.label}` : "⏸ OCR gestoppt"}
          {" — Karte seit "}{stuckMin}{" min in ocr_running"}
        </strong>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8, fontFamily: "monospace", fontSize: 12, opacity: 0.85 }}>
          state=<code>ocr_running</code>
          <StateUuidChip uuid={stateUuid} />
        </span>
      </div>

      {diag && (
        <div style={{ fontSize: 13, fontWeight: 600 }}>
          {diag.advice}
        </div>
      )}
      {!diag && diagLoading && (
        <div style={{ fontSize: 12, fontStyle: "italic", opacity: 0.7 }}>
          Diagnose lädt…
        </div>
      )}

      {diag && (
        <details open={diag.hypothesis !== "running_normal"} style={{ fontSize: 12 }}>
          <summary style={{ cursor: "pointer", fontWeight: 600, marginBottom: 6 }}>
            🔍 Glass-Box-Diagnose
          </summary>
          <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
            <tbody>
              <DiagRow label="pg-boss Job-UUID">
                {diag.job ? <CopyableMono text={diag.job.job_id} /> : <em>kein Job</em>}
              </DiagRow>
              <DiagRow label="pg-boss Job-State">
                {diag.job ? <code>{diag.job.job_state}</code> : <em>—</em>}
              </DiagRow>
              <DiagRow label="Job started_on">
                {diag.job?.started_on ? new Date(diag.job.started_on).toLocaleString("de-AT") : <em>—</em>}
              </DiagRow>
              <DiagRow label="Sidecar aktiv">
                {diag.sidecar?.active ? (
                  <code>{diag.sidecar.active.op_label} → {diag.sidecar.active.last_detail}</code>
                ) : diag.sidecar?.error ? (
                  <em>nicht erreichbar: {diag.sidecar.error}</em>
                ) : <em>idle (keine Operation)</em>}
              </DiagRow>
              {diag.sidecar?.active && (
                <>
                  <DiagRow label="Sidecar elapsed_total">
                    {Math.round(diag.sidecar.active.elapsed_total_s)}s
                  </DiagRow>
                  <DiagRow label="Sekunden ohne Heartbeat">
                    <strong>{Math.round(diag.sidecar.active.elapsed_since_heartbeat_s)}s</strong>
                    {diag.sidecar.active.elapsed_since_heartbeat_s > 300 && (
                      <span style={{ color: "#92400e", marginLeft: 6 }}>(über 5 min — kontrollieren)</span>
                    )}
                  </DiagRow>
                  <DiagRow label="Hard-Timeout in">
                    {Math.max(0, Math.round(diag.sidecar.active.hard_timeout_s - diag.sidecar.active.elapsed_total_s))}s
                  </DiagRow>
                  <DiagRow label="Sidecar Request-ID">
                    <CopyableMono text={diag.sidecar.active.request_id} />
                  </DiagRow>
                </>
              )}
              <DiagRow label="Active Variant (Image-Preprocess)">
                {diag.active_variant && diag.active_variant !== "original" ? (
                  <code>{diag.active_variant}</code>
                ) : <em>original PDF</em>}
              </DiagRow>
              <DiagRow label="Retry-Count">
                {diag.job?.retry_count ?? 0}
              </DiagRow>
              {diag.job?.error_msg && (
                <DiagRow label="Letzte Fehlermeldung">
                  <code style={{ fontSize: 11, wordBreak: "break-all" }}>{diag.job.error_msg.slice(0, 200)}</code>
                </DiagRow>
              )}
              <DiagRow label="Hypothesis">
                <code style={{ fontWeight: 600 }}>{diag.hypothesis}</code>
              </DiagRow>
            </tbody>
          </table>
        </details>
      )}
      {phase === "done" && (
        <div style={{ background: "#ecfdf5", color: "#065f46", border: "1px solid #86efac", padding: 8, borderRadius: 4, fontSize: 13 }}>
          ✅ Restart ausgelöst. Neuer Job: <code style={{ fontSize: 12 }}>{lastJob ?? "—"}</code>
        </div>
      )}
      {phase === "error" && error && (
        <div style={{ background: "#fef2f2", color: "#991b1b", border: "1px solid #fca5a5", padding: 8, borderRadius: 4, fontSize: 13 }}>
          Fehler: {error}
        </div>
      )}
      <div>
        <button
          type="button"
          onClick={restart}
          disabled={phase === "restarting"}
          style={{
            padding: "6px 14px", fontSize: 13, fontWeight: 600,
            background: phase === "restarting" ? "#9ca3af" : "#b91c1c",
            color: "white", border: 0, borderRadius: 4,
            cursor: phase === "restarting" ? "wait" : "pointer",
          }}
          title="Setzt state zurück auf ocr_pending und enqueued einen frischen card.ocr Job"
        >
          {phase === "restarting" ? "Restart läuft …" : "OCR neu starten"}
        </button>
      </div>
    </div>
  );
}

/** 2-Spalten-Diagnose-Row mit Label links + Wert rechts. */
function DiagRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <tr style={{ borderTop: "1px solid #e5e7eb" }}>
      <td style={{ padding: "4px 8px 4px 0", fontWeight: 500, color: "#4b5563", whiteSpace: "nowrap", verticalAlign: "top" }}>{label}</td>
      <td style={{ padding: "4px 0", color: "#1f2937" }}>{children}</td>
    </tr>
  );
}

/** Mono-spaced kopierbarer Text-Block (für Job-IDs, Request-IDs). */
function CopyableMono({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <code
      onClick={(e) => {
        e.preventDefault();
        try { navigator.clipboard?.writeText(text); } catch { /* */ }
        setCopied(true);
        setTimeout(() => setCopied(false), 900);
      }}
      title={`Klick zum Kopieren — ${text}`}
      style={{
        cursor: "pointer",
        padding: "1px 6px", borderRadius: 3,
        background: copied ? "#10b981" : "#fef3c7",
        color: copied ? "white" : "#92400e",
        fontSize: 11, fontWeight: 600,
        userSelect: "none",
      }}
    >
      {copied ? "✓ kopiert" : `⧉ ${text.length > 30 ? text.slice(0, 30) + "…" : text}`}
    </code>
  );
}

/** Klein, kopierbar — adressiert eindeutig diese Karte × State-Kombination. */
function StateUuidChip({ uuid }: { uuid: string }) {
  const [copied, setCopied] = useState(false);
  function copy(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    try {
      if (navigator.clipboard?.writeText) {
        void navigator.clipboard.writeText(uuid).catch(() => {});
      } else {
        const ta = document.createElement("textarea");
        ta.value = uuid; document.body.appendChild(ta); ta.select();
        document.execCommand("copy"); document.body.removeChild(ta);
      }
    } catch { /* */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 900);
  }
  return (
    <code
      onClick={copy}
      title={`Klick zum Kopieren — ${uuid}`}
      style={{
        cursor: "pointer",
        padding: "1px 6px", borderRadius: 3,
        background: copied ? "#10b981" : "#fee2e2",
        color: copied ? "white" : "#991b1b",
        fontSize: 11, fontWeight: 600,
        userSelect: "none",
        whiteSpace: "nowrap",
      }}
    >
      {copied ? "✓ kopiert" : `⧉ ${uuid.slice(0, 8)}`}
    </code>
  );
}
